import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box } from "@mui/material";
import {
  MyTimesheetExaminersCard,
  MyTimesheetMyTimesheet,
  MyTimesheetNewRequestDialog,
  MyTimesheetPageTitle,
  MyTimesheetShowRequestDialog,
  MyTimesheetSummaryCard,
  MyTimesheetTodaysCard,
} from "my-timesheet/components";
import { useMyTimesheetTimesheetList } from "my-timesheet/hooks";
import { OcInfoDialog, OcPageTransition } from "oc/components";
import { useOcCurrentUserState } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

type NewRequestType = {
  name: string;
  description: string;
  projectId: string;
  clientId: string;
  costId: string;
  startTime: Date;
  endTime: Date | null;
};

export const MyTimesheetMyRequestsPage = () => {
  const [newRequest, setNewRequest] = useState<NewRequestType | null>(null);
  const [showRequest, setShowRequest] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [period, setPeriod] = useState("month");

  const handlePeriodChange = (period: string) => {
    setPeriod(period);
  };

  const periodStart = moment(currentDate)
    .startOf(period as "day" | "week" | "month" | "year")
    .toDate();
  const periodEnd = moment(currentDate)
    .endOf(period as "day" | "week" | "month" | "year")
    .toDate();

  const handleChangeCurrentDate = (date: Date) => {
    setCurrentDate(date);
  };

  let { contractCode } = useOcCurrentUserState();

  const {
    data,
    events,
    loading,
    error,
    getRequest,
    removeMyRequest,
    createMyRequest,
    updateMyRequest,
    openedItem,
    dailySums,
    refetch: refetchMysheetTimesheets,
  } = useMyTimesheetTimesheetList({
    contractId: contractCode,
    periodStart: periodStart,
    periodEnd: periodEnd,
    extendPeriod: true,
    role: "user",
  });

  const handleNewRequest = (date: Date) => {
    const dateStr = moment(date).format("YYYY-MM-DD");
    let timeStr = moment(date).format("HH:mm");
    if (timeStr === "00:00") {
      timeStr = moment().format("HH:mm");
    }
    const startTime = new Date(`${dateStr} ${timeStr}`);

    const eventsForDay = events?.filter((event) =>
      moment(date).isBetween(event?.start, event?.end, "day", "[]"),
    );

    if (eventsForDay?.some((event) => event?.id.startsWith("leave-"))) {
      setIsDialogOpen(true);
      return;
    }

    const newRequest = {
      name: "",
      description: "",
      projectId: "",
      clientId: "",
      costId: "",
      startTime,
      endTime: null,
    };
    setNewRequest(newRequest);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleCloseNewRequest = () => {
    setNewRequest(null);
  };

  const handleNewRequestReset = () => {
    setNewRequest(null);
  };

  const handleShowRequest = (_id: string) => {
    const request = getRequest(_id);
    setShowRequest(request);
  };

  const handleShowRequestReset = () => {
    setShowRequest(null);
  };

  return (
    <OcPageTransition>
      <OcInfoDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        text={t("MY_TIMESHEET_RECORDING_ACTIVITIES_NOT_ALLOWED")}
        handleDialogClose={handleDialogClose}
        isMobile={isMobile}
      />
      <MyTimesheetNewRequestDialog
        open={newRequest ? true : false}
        newRequest={newRequest ?? undefined}
        resetNewRequest={handleNewRequestReset}
        createMyRequest={async ({ variables }) => {
          await createMyRequest(variables);
          return Promise.resolve();
        }}
        onClose={handleCloseNewRequest}
        events={events?.map((event) => ({
          ...event,
          end: event.end ?? event.start, // fallback to start if end is null
        }))}
        isMobile={isMobile}
      />
      <MyTimesheetShowRequestDialog
        request={showRequest}
        resetShowRequest={handleShowRequestReset}
        removeMyRequest={removeMyRequest}
        isMobile={isMobile}
      />
      <Box padding={isMobile ? "0px" : "16px"}>
        <MyTimesheetPageTitle
          title={t("MY_TIMESHEET_TIME_TRACKER")}
          showDateRange={false}
          isMobile={isMobile}
          handlePeriodChange={handlePeriodChange}
        />
        <Box height="16px" />
        <Box
          display="flex"
          flexDirection="row"
          alignItems="stretch"
          style={{ flexDirection: isMobile ? "column" : "row" }}
        >
          <Box flex="3" display="flex" flexDirection="column">
            <MyTimesheetTodaysCard
              updateMyRequest={updateMyRequest}
              openedItem={openedItem}
              refetchMysheetTimesheets={refetchMysheetTimesheets}
              removeMyRequest={removeMyRequest}
              events={events}
              isMobile={isMobile}
            />
          </Box>
          {isMobile && <Box height="16px" />}
          <Box
            paddingLeft={isMobile ? "0px" : "16px"}
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            flex="1"
          >
            <Box flex="1" display="flex" flexDirection="column">
              <MyTimesheetSummaryCard dailySums={dailySums} />
            </Box>
            <Box height="16px" />
            <Box display="flex" flexDirection="column">
              <MyTimesheetExaminersCard contractCode={contractCode} />
            </Box>
          </Box>
        </Box>
        <Box height="16px" />
        <MyTimesheetMyTimesheet
          onNewRequest={handleNewRequest}
          data={data}
          events={events}
          loading={loading}
          error={error}
          onShowRequest={handleShowRequest}
          currentDate={currentDate}
          onChangeCurrentDate={handleChangeCurrentDate}
          period={period}
          onChangePeriod={handlePeriodChange}
          openedItem={openedItem}
          isMobile={isMobile}
        />
      </Box>
    </OcPageTransition>
  );
};

export default MyTimesheetMyRequestsPage;
